import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, useMediaQuery, useTheme, CircularProgress, IconButton } from '@mui/material';
import { Image } from 'antd';
import {
    LeftOutlined,
    RightOutlined,
    DownloadOutlined,
    SwapOutlined,
    RotateLeftOutlined,
    RotateRightOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    UndoOutlined,
    PlayCircleFilled,
} from '@ant-design/icons';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface VehicleImage {
    vehicleImageUrl: string;
}

interface VehicleVideo {
    vehicleVideoUrl: string;
}

interface CarImageGalleryProps {
    vehicle: {
        vehicleBrand?: string;
        vehicleImage?: VehicleImage[];
        vehicleVideo?: VehicleVideo[];
    };
    setTabIndex?: (index: number) => void;
    setGalleryExpanded?: (expanded: boolean) => void;
}

type MediaItem = {
    type: 'image' | 'video';
    url: string;
    originalIndex?: number;
};

const CarImageGallery: React.FC<CarImageGalleryProps> = ({
    vehicle,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // --- Prepare Data ---
    const videoList: MediaItem[] = (vehicle?.vehicleVideo || []).map((v) => ({
        type: 'video',
        url: v.vehicleVideoUrl,
    }));

    const imageList: MediaItem[] = (vehicle?.vehicleImage || []).map((i) => ({
        type: 'image',
        url: i.vehicleImageUrl,
    }));

    const mediaList: MediaItem[] = [...videoList, ...imageList];

    const defaultIndex = 0;
    
    const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
    const [lightboxVisible, setLightboxVisible] = useState(false);
    const [mediaLoading, setMediaLoading] = useState(true);
    const [videoPlaying, setVideoPlaying] = useState(false);

    const thumbnailContainerRef = useRef<HTMLDivElement>(null);
    // Explicitly typing the ref array
    const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

    const selectedMedia = mediaList[selectedIndex];

    // --- Handlers ---

    const handleNext = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setSelectedIndex((prev) => (prev + 1) % mediaList.length);
        setVideoPlaying(false);
    };

    const handlePrev = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setSelectedIndex((prev) => (prev - 1 + mediaList.length) % mediaList.length);
        setVideoPlaying(false);
    };

    const handleThumbnailClick = (index: number) => {
        setSelectedIndex(index);
        setVideoPlaying(false);
    };

    const handleMainMediaClick = () => {
        if (selectedMedia.type === 'video') {
            setVideoPlaying(true);
        } else {
            setLightboxVisible(true);
        }
    };

    const handleDownload = (url: string) => {
        const suffix = url.slice(url.lastIndexOf('.'));
        const filename = `vehicle-${Date.now()}${suffix}`;
        fetch(url)
            .then((res) => res.blob())
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                URL.revokeObjectURL(blobUrl);
                link.remove();
            });
    };

    // --- Effects ---

    // 1. Scroll active thumbnail into view
    useEffect(() => {
        const currentThumb = thumbnailRefs.current[selectedIndex];
        if (currentThumb && thumbnailContainerRef.current) {
            currentThumb.scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
            });
        }
    }, [selectedIndex]);

    // 2. Reset loading state when index changes
    useEffect(() => {
        setMediaLoading(true);
    }, [selectedIndex]);

    // 3. ⚡ PERFORMANCE: Preload the NEXT image in the background
    useEffect(() => {
        const nextIndex = (selectedIndex + 1) % mediaList.length;
        const nextItem = mediaList[nextIndex];
        
        // Only preload if it's an image
        if (nextItem && nextItem.type === 'image') {
            const img = new globalThis.Image();
            img.src = nextItem.url;
        }
    }, [selectedIndex, mediaList]);

    // 4. Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (lightboxVisible || mediaList.length === 0) return;
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'ArrowLeft') handlePrev();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxVisible, mediaList.length]);

    const currentImageIndexInLightbox = imageList.findIndex(img => img.url === selectedMedia?.url);
    const initialImageIndex = currentImageIndexInLightbox >= 0 ? currentImageIndexInLightbox : 0;

    const iconStyle: React.CSSProperties = { color: '#fff', fontSize: 18, cursor: 'pointer' };

    if (mediaList.length === 0) return null;

    return (
        <Grid size={{xs: 12, md: 7}}>
            <Box sx={{ width: '100%' }}>
                
                {/* --- Main Display Area --- */}
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '4/3',
                        backgroundColor: '#000',
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 3,
                        mb: 2,
                        '&:hover .nav-arrow': { opacity: 1 },
                    }}
                >
                    {/* Loading Spinner */}
                    {mediaLoading && (
                        <Box sx={{ position: 'absolute', zIndex: 5 }}>
                            <CircularProgress sx={{ color: theme.palette.primary.main }} />
                        </Box>
                    )}

                    {selectedMedia.type === 'image' && (
                        <Box
                            component="img"
                            src={selectedMedia.url}
                            alt="Vehicle"
                            onLoad={() => setMediaLoading(false)}
                            onClick={handleMainMediaClick}
                            decoding="async" // ⚡ Helps with performance
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                cursor: 'zoom-in',
                                transition: 'opacity 0.3s ease',
                                opacity: mediaLoading ? 0 : 1,
                            }}
                        />
                    )}

                    {selectedMedia.type === 'video' && (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: videoPlaying ? 'default' : 'pointer',
                            }}
                            onClick={handleMainMediaClick}
                        >
                            <Box
                                component="video"
                                src={selectedMedia.url}
                                autoPlay={false}
                                muted={!videoPlaying}
                                controls={videoPlaying}
                                onLoadedData={() => setMediaLoading(false)}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    opacity: mediaLoading ? 0 : 1,
                                }}
                            />
                            {!videoPlaying && !mediaLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        zIndex: 4,
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        borderRadius: '50%',
                                        p: 2,
                                        display: 'flex',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'scale(1.1)', bgcolor: 'rgba(0,0,0,0.7)' }
                                    }}
                                >
                                    <PlayCircleFilled style={{ fontSize: 48, color: '#fff' }} />
                                </Box>
                            )}
                        </Box>
                    )}

                    {!isMobile && (
                        <>
                            <IconButton
                                className="nav-arrow"
                                onClick={handlePrev}
                                sx={{
                                    position: 'absolute',
                                    left: 10,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255,255,255,0.8)',
                                    color: '#000',
                                    opacity: 0,
                                    transition: 'opacity 0.3s',
                                    '&:hover': { bgcolor: '#fff' },
                                    zIndex: 10
                                }}
                            >
                                <ArrowBackIosNewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                className="nav-arrow"
                                onClick={handleNext}
                                sx={{
                                    position: 'absolute',
                                    right: 10,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255,255,255,0.8)',
                                    color: '#000',
                                    opacity: 0,
                                    transition: 'opacity 0.3s',
                                    '&:hover': { bgcolor: '#fff' },
                                    zIndex: 10
                                }}
                            >
                                <ArrowForwardIosIcon fontSize="small" />
                            </IconButton>
                        </>
                    )}
                </Box>

                {/* --- Thumbnails Strip --- */}
                <Box
                    ref={thumbnailContainerRef}
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        overflowX: 'auto',
                        py: 1,
                        px: 0.5,
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': { height: 6 },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#ccc',
                            borderRadius: 3,
                        },
                    }}
                >
                    {mediaList.map((item, idx) => {
                        const isActive = idx === selectedIndex;
                        return (
                            <Box
                                key={idx}
                                // ✅ FIX: Added braces and type casting to solve TS error
                                ref={(el) => { thumbnailRefs.current[idx] = el as HTMLDivElement | null; }}
                                onClick={() => handleThumbnailClick(idx)}
                                sx={{
                                    flexShrink: 0,
                                    width: { xs: 80, md: 100 },
                                    height: { xs: 60, md: 75 },
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    border: isActive 
                                        ? `3px solid ${theme.palette.primary.main}` 
                                        : '1px solid transparent',
                                    opacity: isActive ? 1 : 0.6,
                                    transition: 'all 0.2s',
                                    '&:hover': { opacity: 1 },
                                }}
                            >
                                {item.type === 'video' ? (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            bgcolor: '#000',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <PlayCircleFilled style={{ color: '#fff', fontSize: 24 }} />
                                    </Box>
                                ) : (
                                    <Box
                                        component="img"
                                        src={item.url}
                                        loading="lazy"
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* --- Ant Design Lightbox (Images Only) --- */}
                <Box sx={{ display: 'none' }}>
                    <Image.PreviewGroup
                        preview={{
                            visible: lightboxVisible,
                            current: initialImageIndex,
                            onVisibleChange: (vis) => setLightboxVisible(vis),
                            onChange: (currentImageIdx) => {
                                const globalIndex = mediaList.findIndex(m => m.url === imageList[currentImageIdx].url);
                                if (globalIndex !== -1) setSelectedIndex(globalIndex);
                            },
                            toolbarRender: (
                                _,
                                { transform: { scale }, actions: { onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onReset } }
                            ) => (
                                <Box
                                    sx={{
                                        position: 'fixed',
                                        bottom: 50,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                        padding: '10px 20px',
                                        borderRadius: 8,
                                        zIndex: 2000,
                                        display: 'flex',
                                        gap: 3,
                                    }}
                                >
                                    <DownloadOutlined style={iconStyle} onClick={() => handleDownload(mediaList[selectedIndex].url)} />
                                    <SwapOutlined rotate={90} style={iconStyle} onClick={onFlipY} />
                                    <SwapOutlined style={iconStyle} onClick={onFlipX} />
                                    <RotateLeftOutlined style={iconStyle} onClick={onRotateLeft} />
                                    <RotateRightOutlined style={iconStyle} onClick={onRotateRight} />
                                    <ZoomOutOutlined style={iconStyle} onClick={onZoomOut} disabled={scale === 1} />
                                    <ZoomInOutlined style={iconStyle} onClick={onZoomIn} disabled={scale === 50} />
                                    <UndoOutlined style={iconStyle} onClick={onReset} />
                                </Box>
                            ),
                        }}
                    >
                        {imageList.map((img, idx) => (
                            <Image key={idx} src={img.url} />
                        ))}
                    </Image.PreviewGroup>
                </Box>
            </Box>
        </Grid>
    );
};

export default CarImageGallery;